import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { Dialog, DialogTitle, DialogActions, Button, IconButton, Box, Typography, FormControlLabel, Switch } from '@mui/material';
import { Close, Delete, CameraAlt } from '@mui/icons-material';
import ExpertModalForm from 'src/pages/Experts/components/ExpertModalForm';
import toast from 'react-hot-toast';
import { expertApi } from 'src/api/expertApi';

const EditExpertModal = ({ open, onClose, expert, onSave }) => {
  const avatarUploadRef = useRef(null);

  const parseContactsJson = (data) => {
    if (data === '[object Object]') {
      return [];
    }
    try {
      return JSON.parse(data || '[]');
    } catch (e) {
      return [];
    }
  };

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    position: '',
    company: '',
    bio: '',
    hashtags: '',
    pointOfContactJson: [],
    reportsContactsJson: [],
    promptModify: '',
    location: '',
    imageURL: 'https://ucarecdn.com/52021bd1-5a2d-48f9-b7d3-6c0730fc039d/',
    qwotedURL: '',
    companyURL: '',
    linkedinURL: '',
    twitterURL: '',
    websiteURL: '',
    isActive: 0,
    editStates: {},
  });

  useEffect(() => {
    setFormData({
      name: expert?.name || '',
      email: expert?.email || '',
      position: expert?.position || '',
      company: expert?.company || '',
      bio: expert?.bio || '',
      hashtags: expert?.hashtags || '',
      pointOfContactJson: parseContactsJson(expert?.pointOfContactJson),
      reportsContactsJson: parseContactsJson(expert?.reportsContactsJson),
      promptModify: expert?.promptModify || '',
      location: expert?.location || '',
      imageURL: expert?.imageURL || 'https://ucarecdn.com/52021bd1-5a2d-48f9-b7d3-6c0730fc039d/',
      qwotedURL: expert?.qwotedURL || '',
      companyURL: expert?.companyURL || '',
      linkedinURL: expert?.linkedinURL || '',
      twitterURL: expert?.twitterURL || '',
      websiteURL: expert?.websiteURL || '',
      isActive: expert?.isActive ? 1 : 0,
      editStates: {},
    });
  }, [expert]);

  const handleChange = (field, value) => {
    setFormData((prev) => {
      const updated = { ...prev, [field]: value };

      // If imageURL is being changed, mark it as edited
      if (field === 'imageURL') {
        updated.editStates = { ...prev.editStates, imageURL: true };
      }

      return updated;
    });
  };

  const handleAddPoC = () => {
    setFormData((prev) => ({
      ...prev,
      pointOfContactJson: [...prev.pointOfContactJson, { id: Date.now(), firstName: '', lastName: '', email: '' }],
    }));
  };

  const handleRemovePoC = (id) => {
    setFormData((prev) => ({
      ...prev,
      pointOfContactJson: prev.pointOfContactJson.filter((poc) => poc.id !== id),
    }));
  };

  const handleUpdatePoC = (id, field, value) => {
    setFormData((prev) => ({
      ...prev,
      pointOfContactJson: prev.pointOfContactJson.map((poc) => {
        if (poc.id === id) {
          return { ...poc, [field]: value };
        }
        return poc;
      }),
    }));
  };

  const handleAddReportContact = () => {
    setFormData((prev) => ({
      ...prev,
      reportsContactsJson: [...prev.reportsContactsJson, { id: Date.now(), firstName: '', lastName: '', email: '' }],
    }));
  };

  const handleRemoveReportContact = (id) => {
    setFormData((prev) => ({
      ...prev,
      reportsContactsJson: prev.reportsContactsJson.filter((contact) => contact.id !== id),
    }));
  };

  const handleUpdateReportContact = (id, field, value) => {
    setFormData((prev) => ({
      ...prev,
      reportsContactsJson: prev.reportsContactsJson.map((contact) => {
        if (contact.id === id) {
          return { ...contact, [field]: value };
        }
        return contact;
      }),
    }));
  };

  const handleDone = async () => {
    try {
      const editedFields = Object.keys(formData.editStates || {}).filter((field) => formData.editStates[field] === true);

      if (editedFields.length === 0) {
        onClose();
        return;
      }

      const dataToSave = {};

      editedFields.forEach((field) => {
        if (field === 'pointOfContactJson') {
          dataToSave[field] = JSON.stringify(formData.pointOfContactJson);
        } else if (field === 'reportsContactsJson') {
          dataToSave[field] = JSON.stringify(formData.reportsContactsJson);
        } else {
          dataToSave[field] = formData[field];
        }
      });

      // Always include email and pointOfContactJson if they exist to prevent backend validation errors
      if (formData.email) {
        dataToSave.email = formData.email;
      }
      if (formData.pointOfContactJson) {
        dataToSave.pointOfContactJson = JSON.stringify(formData.pointOfContactJson);
      }

      await onSave(expert.id, dataToSave);

      setFormData((prev) => ({
        ...prev,
        editStates: {},
      }));

      toast.success('Changes saved successfully');
      onClose();
    } catch (error) {
      toast.error('Failed to save changes: ' + error.message);
    }
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this expert? This action cannot be undone.')) {
      try {
        await expertApi.removeExpert(expert.id);
        toast.success('Expert deleted successfully');
        onClose();
      } catch (error) {
        toast.error('Failed to delete expert: ' + error.message);
      }
    }
  };

  const handleAvatarClick = () => {
    if (avatarUploadRef.current) {
      avatarUploadRef.current.triggerUpload();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: '12px',
          margin: { xs: '8px', sm: '16px' },
          width: { xs: 'calc(100% - 16px)', sm: 'calc(100% - 32px)' },
          '& .MuiDialogContent-root': {
            padding: { xs: '12px', sm: '24px' },
          },
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          p: { xs: 2, sm: 3 },
          borderBottom: '1px solid rgba(0, 0, 0, 0.12)',
          flexDirection: { xs: 'column', sm: 'row' },
          gap: { xs: 2, sm: 0 },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            width: '100%',
            flexDirection: { xs: 'column', sm: 'row' },
            textAlign: { xs: 'center', sm: 'left' },
            gap: { xs: 1, sm: 0 },
          }}
        >
          <Box
            sx={{
              width: { xs: 80, sm: 60 },
              height: { xs: 80, sm: 60 },
              borderRadius: '16px',
              overflow: 'hidden',
              mr: { xs: 0, sm: 3 },
              mb: { xs: 1, sm: 0 },
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              position: 'relative',
              cursor: 'pointer',
              '&:hover': {
                '& .camera-overlay': {
                  opacity: 1,
                },
                transform: 'scale(1.02)',
              },
              transition: 'transform 0.2s ease-in-out',
            }}
            onClick={handleAvatarClick}
          >
            <img src={formData.imageURL} alt="avatar" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
            <Box
              className="camera-overlay"
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                backgroundColor: 'rgba(0,0,0,0.5)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: 0,
                transition: 'opacity 0.2s ease-in-out',
              }}
            >
              <CameraAlt sx={{ color: 'white', fontSize: { xs: 24, sm: 20 } }} />
            </Box>
          </Box>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 600, fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
              {formData.name}
            </Typography>
            <Typography
              variant="h6"
              color="text.secondary"
              sx={{
                fontWeight: 400,
                fontSize: { xs: '0.9rem', sm: '1.25rem' },
                mt: { xs: 0.5, sm: 0 },
              }}
            >
              {formData.position} {formData.company && `at ${formData.company}`}
            </Typography>
          </Box>
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            color: 'text.secondary',
            display: { xs: 'none', sm: 'block' },
            top: { xs: 8, sm: 'auto' },
            right: { xs: 8, sm: 'auto' },
          }}
        >
          <Close />
        </IconButton>
      </DialogTitle>

      <ExpertModalForm
        ref={avatarUploadRef}
        formData={{
          ...formData,
          isActive: formData.isActive === 1,
        }}
        onChange={handleChange}
        onAddPoC={handleAddPoC}
        onRemovePoC={handleRemovePoC}
        onUpdatePoC={handleUpdatePoC}
        onAddReportContact={handleAddReportContact}
        onRemoveReportContact={handleRemoveReportContact}
        onUpdateReportContact={handleUpdateReportContact}
        editMode={false}
      />

      <DialogActions
        sx={{
          p: { xs: 2, sm: 3 },
          borderTop: '1px solid rgba(0, 0, 0, 0.12)',
          flexDirection: { xs: 'column', sm: 'row' },
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: { xs: 2, sm: 0 },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: { xs: 'space-between', sm: 'flex-start' },
            width: { xs: '100%', sm: 'auto' },
            gap: 2,
          }}
        >
          <FormControlLabel
            control={
              <Switch
                checked={formData.isActive === 1}
                onChange={async (e) => {
                  const newValue = e.target.checked ? 1 : 0;
                  const dataToSave = { isActive: newValue };

                  // Always include email and pointOfContactJson if they exist to prevent backend validation errors
                  if (formData.email) {
                    dataToSave.email = formData.email;
                  }
                  if (formData.pointOfContactJson) {
                    dataToSave.pointOfContactJson = JSON.stringify(formData.pointOfContactJson);
                  }

                  await onSave(expert.id, dataToSave);
                  handleChange('isActive', newValue);
                }}
                color="primary"
              />
            }
            label={formData.isActive === 1 ? 'Active' : 'Client offboarded'}
          />
          <Button onClick={handleDelete} variant="outlined" color="error" startIcon={<Delete />} size="small">
            Delete Expert
          </Button>
        </Box>
        <Box sx={{ flex: { xs: 0, sm: 1 } }} />
        <Button
          onClick={handleDone}
          variant="contained"
          fullWidth={window.innerWidth < 600}
          sx={{
            mt: { xs: 1, sm: 0 },
            width: { xs: '100%', sm: 'auto' },
          }}
        >
          {(() => {
            const editedFieldsCount = Object.keys(formData.editStates || {}).filter((field) => formData.editStates[field] === true).length;

            if (editedFieldsCount === 0) {
              return 'Done';
            } else {
              return `Save Changes (${editedFieldsCount})`;
            }
          })()}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

EditExpertModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  expert: PropTypes.object.isRequired,
  onSave: PropTypes.func.isRequired,
};

export default EditExpertModal;
